import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import {
    InvalidateQueries,
    PortfolioCentralRateGroupGetAssignableEnterpriseGroupsParameters,
    PortfolioCentralRateGroupGetAssignableEnterprisesParameters,
    useRepositoryMutation,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { WithRateGroupId } from '@mews-commander/types';
import { createActorProfilesMapper } from '@mews-commander/utils';
import { createPaymentPolicyMapper } from '../../../../../rateGroups/queries/utils';

export const rateGroupsQueriesToInvalidate: InvalidateQueries = {
    portfolioCentralRateGroups: ['getAll', 'getOverview', 'getAssignableEnterprises'],
    portfolioRateGroups: ['getOverview'],
    rateGroups: ['getAll'],
};

export const useGetAssignableEnterprises = (params: PortfolioCentralRateGroupGetAssignableEnterprisesParameters) =>
    useRepositoryQuery('portfolioCentralRateGroups', 'getAssignableEnterprises', params);

export const useGetAssignablePropertyGroups = (
    params: PortfolioCentralRateGroupGetAssignableEnterpriseGroupsParameters
) => useRepositoryQuery('portfolioCentralRateGroups', 'getAssignableEnterpriseGroups', params);

export const useCentralRateGroupWithProfilesGet = (
    { rateGroupId }: Partial<WithRateGroupId>,
    { enabled }: { enabled: boolean }
) =>
    useRepositoryQuery(
        'portfolioCentralRateGroups',
        'getAll',
        {
            centralRateGroupIds: rateGroupId ? [rateGroupId] : null,
        },
        {
            enabled: rateGroupId != null && enabled,
            select: ({ centralRateGroups, profiles, ...others }) => {
                const centralRateGroup = centralRateGroups[0];
                return centralRateGroup
                    ? {
                          ...centralRateGroup,
                          creatorProfile: profiles?.find((profile) => profile.id === centralRateGroup.creatorProfileId),
                          updaterProfile: profiles?.find((profile) => profile.id === centralRateGroup.updaterProfileId),
                          ...others,
                      }
                    : undefined;
            },
        }
    );

export const useLocalRateGroupWithDefaultPoliciesGet = ({
    rateGroupId,
    enterpriseId,
}: {
    rateGroupId: string | undefined;
    enterpriseId: string | undefined;
}) =>
    useRepositoryQuery(
        'rateGroups',
        'getAll',
        {
            enterpriseIds: [enterpriseId ?? ''],
            rateGroupIds: rateGroupId ? [rateGroupId] : null,
            extent: {
                paymentPolicies: true,
            },
        },
        {
            enabled: !!(enterpriseId && rateGroupId),
            select: ({ rateGroups, settlementRules, cancellationPolicies, profiles }) =>
                pipe(
                    rateGroups,
                    array.findFirst(({ id }) => id === rateGroupId),
                    option.map((item) =>
                        pipe(
                            item,
                            createPaymentPolicyMapper({
                                cancellationPolicies: cancellationPolicies ?? [],
                                settlementRules: settlementRules ?? [],
                            }),
                            createActorProfilesMapper(profiles)
                        )
                    ),
                    option.getOrElseW(() => undefined)
                ),
        }
    );

export const useAssignProperties = () =>
    useRepositoryMutation('portfolioCentralRateGroups', 'assignEnterprises', {
        invalidateQueries: rateGroupsQueriesToInvalidate,
    });

export const useUnassignProperties = () =>
    useRepositoryMutation('portfolioCentralRateGroups', 'unassignEnterprises', {
        invalidateQueries: rateGroupsQueriesToInvalidate,
    });
